import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { seoConfig, defaultSEO, websiteStructuredData } from '../config/seoConfig';

const useSEO = (customSEO = {}) => {
  const location = useLocation();
  const path = location.pathname;

  const getSEOForRoute = () => {
    // Auth & noindex pages
    if (path === '/login') return seoConfig.login;
    if (path === '/sign-up') return seoConfig.signUp;
    if (path === '/account-disabled') return seoConfig.accountDisabled;

    // Checkout
    if (path.startsWith('/success/checkout')) return seoConfig.checkoutSuccess;
    if (path === '/transaction-failure') return seoConfig.checkoutFailure;

    // E-Invoice (public)
    if (path.startsWith('/e-invoice/')) return seoConfig.eInvoice;

    // Reconciliation
    if (path.startsWith('/reconciliation-verification/')) return seoConfig.reconciliationVerification;

    // Error pages
    if (path.startsWith('/error/add-blocker-detected')) return seoConfig.adBlockerDetected;
    if (path.startsWith('/error/')) return seoConfig.surveyNotLive;

    // Legal & public
    if (path === '/terms-and-conditions') return seoConfig.termsAndConditions;
    if (path === '/privacy-policy') return seoConfig.privacyPolicy;
    if (path === '/refund-policy') return seoConfig.refundPolicy;

    // Help Center
    if (path.startsWith('/help-center')) return seoConfig.helpCenter;

    // Onboarding
    if (path === '/onboarding') return seoConfig.onboarding;

    // Main app routes
    if (path === '/') return seoConfig.dashboard;
    if (path === '/surveys') return seoConfig.surveys;
    if (path === '/survey-groups') return seoConfig.surveyGroups;
    if (path === '/feasibility') return seoConfig.feasibility;
    if (path === '/qualification-library') return seoConfig.qualificationLibrary;
    if (path === '/invoice-billing') return seoConfig.invoiceBilling;
    if (path === '/payment/account') return seoConfig.paymentAccount;
    if (path === '/payment' || path === '/payment/') return seoConfig.payment;

    // Survey details: /surveys/:id or /surveys/:id/:type
    if (path.startsWith('/surveys/') && path.split('/').filter(Boolean).length >= 2) {
      const segments = path.split('/').filter(Boolean);
      const surveyId = segments[1];
      const surveyName = segments[2] || `Survey ${surveyId}`;
      return seoConfig.surveyDetails(surveyId, surveyName);
    }

    // Survey group edit: /survey-groups/:id
    if (path.startsWith('/survey-groups/')) return seoConfig.surveyGroupEdit;

    // Invoice detail: /invoice-billing/:id
    if (path.startsWith('/invoice-billing/')) return seoConfig.invoiceDetail;

    // Profile and sub-routes
    if (path.includes('/profile/api-documentation')) return seoConfig.apiDocumentation;
    if (path.includes('/profile/redirects-and-keys')) return seoConfig.redirectsAndKeys;
    if (path.startsWith('/profile')) return seoConfig.profile;

    // Survey taking (DIY, redirect endpoint, etc.)
    if (path.includes('/DIY/') && path.includes('/gdpr-consent')) return seoConfig.gdprConsent;
    if (path.includes('/DIY/')) return seoConfig.surveyTaking;
    if (path.includes('/7e08091a73b14e034889265e41ba796f91c766ad/')) return seoConfig.surveyTaking;
    if (path.includes('/21df0c3543ff3bd23cecdaf921ff4a704a113d06dc1ec21045a186ad0f2f9a42')) return seoConfig.surveyTaking;

    return defaultSEO;
  };

  const seoData = useMemo(
    () => ({
      ...defaultSEO,
      ...getSEOForRoute(),
      ...customSEO,
      structuredData: websiteStructuredData,
    }),
    [path]
  );

  return seoData;
};

export default useSEO;
